// Figma-style "Interaction" panel for graded ElementNode outcome chips.
// Opened by the +correct / +wrong / +click chips on ElementNode.
// Structure: header · trigger row (read-only) · action row (custom dropdown)
//            · destination row (conditional) · footer add button.
import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import i18n from "i18next";
import { createPortal } from "react-dom";

/* eslint-disable react/prop-types */

const DISPLAYABLE_KINDS = new Set(["VisualImage", "VisualLabel", "EndScreen", "FlipCard"]);
const KIND_SHORT = {
  VisualImage: i18n.t("ItemProps.Kind.VisualImage") || "Görsel",
  VisualLabel: i18n.t("ItemProps.Kind.ShortLabel") || "Etiket",
  EndScreen: i18n.t("ItemProps.Kind.EndScreen") || "Bitiş Ekranı",
  FlipCard: i18n.t("ItemProps.Kind.FlipCard") || "Flip Kart",
};

function getSceneList() {
  try {
    return typeof window.__getScenes === "function" ? window.__getScenes() : [];
  } catch {
    return [];
  }
}

// ---- Action catalog (adapted from Figma's action list) ----------------------
// null entries render as visual separators inside the dropdown.
const ACTION_OPTIONS = [
  { kind: "none", icon: "block", label: i18n.t("ItemProps.Action.None") || "None" },
  null,
  {
    kind: "resume",
    icon: "play_circle",
    label: i18n.t("ItemProps.Action.ResumeLabel") || "Devam Et",
    hint: i18n.t("ItemProps.Action.ResumeHint") || "Timeline'ı sürdür",
  },
  {
    kind: "complete",
    icon: "check_circle",
    label: i18n.t("ItemProps.Action.CompleteLabel") || "Tamamla",
    hint: i18n.t("ItemProps.Action.CompleteHint") || "Etkileşimi sonlandır",
  },
  null,
  {
    kind: "showItem",
    icon: "visibility",
    label: i18n.t("ItemProps.Action.ShowItemLabel") || "Elementi Göster",
    hint: i18n.t("ItemProps.Action.ShowItemHint") || "Seçili öğeyi görünür yapar",
    needsTarget: "item",
  },
  {
    kind: "hideItem",
    icon: "visibility_off",
    label: i18n.t("ItemProps.Action.HideItemLabel") || "Elementi Gizle",
    hint: i18n.t("ItemProps.Action.HideItemHint") || "Seçili öğeyi gizler",
    needsTarget: "item",
  },
  null,
  {
    kind: "jumpScene",
    icon: "arrow_forward",
    label: i18n.t("ItemProps.Action.JumpSceneLabel") || "Sahneye Git",
    hint: i18n.t("ItemProps.Action.JumpSceneHint") || "Belirtilen sahneye atla",
    needsTarget: "scene",
  },
];

const ACTION_BY_KIND = Object.fromEntries(ACTION_OPTIONS.filter(Boolean).map((a) => [a.kind, a]));

// ---- Trigger display config -------------------------------------------------
const TRIGGER_CONFIG = {
  correct: { icon: "check", label: i18n.t("ItemProps.OnCorrectShort") || "Doğruysa" },
  wrong: { icon: "close", label: i18n.t("ItemProps.OnWrongShort") || "Yanlışsa" },
  click: { icon: "touch_app", label: i18n.t("ItemProps.OnClickShort") || "Tıklanınca" },
};

// ---- Positioning ------------------------------------------------------------
function computePos(anchorEl) {
  if (!anchorEl || typeof anchorEl.getBoundingClientRect !== "function") {
    return { top: 120, left: 120 };
  }
  const rect = anchorEl.getBoundingClientRect();
  const panelW = 272;
  const panelH = 280;
  let left = rect.right + 12;
  let top = rect.top + rect.height / 2 - panelH / 2;
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;
  if (left + panelW > vw - 8) left = Math.max(8, rect.left - panelW - 12);
  top = Math.max(8, Math.min(top, vh - panelH - 8));
  return { top, left };
}

export default function OutcomeActionPicker({ anchorEl, outcome, allItems, onPick, onClose }) {
  const panelRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [actionKind, setActionKind] = useState("none");
  const [targetId, setTargetId] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useLayoutEffect(() => {
    setPos(computePos(anchorEl));
  }, [anchorEl]);

  // Outside-click + Escape close
  useEffect(() => {
    const onMouseDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose && onClose();
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (dropdownOpen) {
          setDropdownOpen(false);
        } else {
          onClose && onClose();
        }
      }
    };
    document.addEventListener("mousedown", onMouseDown, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onMouseDown, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [onClose, dropdownOpen]);

  const displayable = (allItems || []).filter((it) => it && DISPLAYABLE_KINDS.has(it.kind));
  const sceneList = getSceneList();

  const selectedAction = ACTION_BY_KIND[actionKind] || ACTION_BY_KIND["none"];
  const needsTarget = selectedAction && selectedAction.needsTarget;
  const canAdd = actionKind !== "none" && (!needsTarget || targetId);

  const handleAdd = () => {
    if (!canAdd) return;
    let action = { kind: actionKind };
    if (actionKind === "showItem" || actionKind === "hideItem") action.itemId = targetId;
    if (actionKind === "jumpScene") action.target = targetId;
    onPick && onPick(action);
    onClose && onClose();
  };

  const handleSelectAction = (kind) => {
    setActionKind(kind);
    setTargetId("");
    setDropdownOpen(false);
  };

  const itemPreview = (it) => {
    const p = it.params || {};
    const raw = p.text ?? it.text ?? p.imageUrl ?? it.imageUrl ?? it.kind;
    const kl = KIND_SHORT[it.kind] || it.kind;
    return `${kl}: ${String(raw).slice(0, 24) || it.id}`;
  };

  const triggerCfg = TRIGGER_CONFIG[outcome] || { icon: "bolt", label: outcome };

  if (typeof document === "undefined") return null;

  const node = (
    <div
      ref={panelRef}
      className="ia-panel"
      style={{ top: pos.top, left: pos.left }}
      role="dialog"
      aria-label={i18n.t("ItemProps.InteractionPanel") || "Etkileşim paneli"}
      data-testid="outcome-action-picker"
    >
      {/* ---- Header ---- */}
      <div className="ia-panel__header">
        <span className="ia-panel__title">{i18n.t("ItemProps.Interaction") || "Etkileşim"}</span>
        <button
          type="button"
          className="ia-panel__close nodrag"
          onClick={onClose}
          aria-label={i18n.t("Close") || "Kapat"}
        >
          <i className="material-icons">close</i>
        </button>
      </div>

      {/* ---- Trigger row (read-only) ---- */}
      <div className="ia-panel__body">
        <div className="ia-panel__row">
          <span className="ia-panel__row-label">
            {i18n.t("ItemProps.Trigger") || "Tetikleyici"}
          </span>
          <div className="ia-panel__field ia-panel__field--readonly">
            <i className="material-icons ia-panel__field-icon">{triggerCfg.icon}</i>
            <span className="ia-panel__field-text">{triggerCfg.label}</span>
          </div>
        </div>

        <div className="ia-panel__sep" />

        {/* ---- Action row ---- */}
        <div className="ia-panel__row">
          <span className="ia-panel__row-label">{i18n.t("ItemProps.ActionLabel") || "Eylem"}</span>
          <button
            type="button"
            className={`ia-panel__field ia-panel__field--select nodrag${dropdownOpen ? " ia-panel__field--open" : ""}`}
            onClick={() => setDropdownOpen((v) => !v)}
            aria-haspopup="listbox"
            aria-expanded={dropdownOpen}
          >
            <i className="material-icons ia-panel__field-icon">{selectedAction.icon}</i>
            <span className="ia-panel__field-text">{selectedAction.label}</span>
            <i className="material-icons ia-panel__field-chevron">
              {dropdownOpen ? "expand_less" : "expand_more"}
            </i>
          </button>
        </div>

        {/* ---- Action dropdown list ---- */}
        {dropdownOpen && (
          <ul className="ia-panel__option-list nodrag" role="listbox">
            {ACTION_OPTIONS.map((opt, i) =>
              opt === null ? (
                <li key={`sep-${i}`} className="ia-panel__option-sep" role="separator" />
              ) : (
                <li key={opt.kind} role="option" aria-selected={opt.kind === actionKind}>
                  <button
                    type="button"
                    className={`ia-panel__option nodrag${opt.kind === actionKind ? " ia-panel__option--active" : ""}`}
                    onClick={() => handleSelectAction(opt.kind)}
                  >
                    {opt.kind === actionKind ? (
                      <i className="material-icons ia-panel__option-check">check</i>
                    ) : (
                      <span className="ia-panel__option-check-placeholder" />
                    )}
                    <i className="material-icons ia-panel__option-icon">{opt.icon}</i>
                    <span className="ia-panel__option-label">{opt.label}</span>
                  </button>
                </li>
              )
            )}
          </ul>
        )}

        {/* ---- Destination row (conditional) ---- */}
        {!dropdownOpen && needsTarget === "item" && (
          <div className="ia-panel__row">
            <span className="ia-panel__row-label">{i18n.t("ItemProps.Target") || "Hedef"}</span>
            <div className="ia-panel__field ia-panel__field--select-native">
              {displayable.length > 0 ? (
                <select
                  className="ia-panel__native-select nodrag"
                  value={targetId}
                  onChange={(e) => setTargetId(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                >
                  <option value="">{i18n.t("ItemProps.SelectPlaceholder") || "— Seç —"}</option>
                  {displayable.map((it) => (
                    <option key={it.id} value={it.id}>
                      {itemPreview(it)}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="ia-panel__field-empty">
                  {i18n.t("ItemProps.NoDisplayableItems") || "Görüntülenebilir öğe yok"}
                </span>
              )}
            </div>
          </div>
        )}

        {!dropdownOpen && needsTarget === "scene" && (
          <div className="ia-panel__row">
            <span className="ia-panel__row-label">{i18n.t("ItemProps.Scene") || "Sahne"}</span>
            <div className="ia-panel__field ia-panel__field--select-native">
              {sceneList.length > 0 ? (
                <select
                  className="ia-panel__native-select nodrag"
                  value={targetId}
                  onChange={(e) => setTargetId(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                >
                  <option value="">{i18n.t("ItemProps.SelectScene") || "— Sahne seç —"}</option>
                  {sceneList.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.title || s.id}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="ia-panel__field-empty">
                  {i18n.t("ItemProps.NoScenesFound") || "Sahne bulunamadı"}
                </span>
              )}
            </div>
          </div>
        )}

        {/* ---- Footer ---- */}
        {!dropdownOpen && (
          <>
            <div className="ia-panel__sep" />
            <div className="ia-panel__footer">
              <button
                type="button"
                className="ia-panel__add nodrag"
                disabled={!canAdd}
                onClick={handleAdd}
              >
                {i18n.t("ItemProps.AddAction") || "Eylem Ekle"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
